
/**
 * Simple in-memory cache implementation
 */

export interface CacheOptions {
  ttl: number; // Time to live in milliseconds
}

export class Cache<T> {
  private cache: Map<string, { data: T, expiry: number }> = new Map();
  private defaultTtl: number;

  constructor(options: CacheOptions = { ttl: 60000 }) {
    this.defaultTtl = options.ttl;
  }

  /**
   * Get data from cache
   * @param key Cache key
   * @returns Cached data or null if not found/expired
   */
  get(key: string): T | null {
    const item = this.cache.get(key);
    
    // Return null if item doesn't exist or has expired
    if (!item || item.expiry < Date.now()) {
      if (item) {
        // Clean up expired item
        this.cache.delete(key);
      }
      return null;
    }
    
    return item.data;
  }

  /**
   * Set data in cache with expiration
   * @param key Cache key
   * @param data Data to store
   * @param ttl Time to live in milliseconds (optional, uses default if not provided)
   */
  set(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      expiry: Date.now() + (ttl || this.defaultTtl)
    });
  }

  /**
   * Check if key exists and is not expired
   * @param key Cache key
   * @returns True if valid entry exists
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    return !!item && item.expiry > Date.now();
  }

  /**
   * Delete cache entry
   * @param key Cache key
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const dataCache = new Cache<any>({ ttl: 5 * 60 * 1000 }); // 5 minutes TTL by default
